import { AdvancedSearchEntityType, WorkflowStatus } from "generated/graphql";
import { stepKey, WorkflowBlueprint, WorkflowTriggerKind } from "./workflow-blueprint";

const UNRECOGNIZED = "unrecognized";

export interface WorkflowEventSource {
    id: string;
    attachTo: AdvancedSearchEntityType;
    status: WorkflowStatus;
}

export type WorkflowEventProperties = {
    workflowId: string;
    entityType: AdvancedSearchEntityType;
    status: WorkflowStatus;
    triggerKind: WorkflowTriggerKind | typeof UNRECOGNIZED;
    step: string;
};

export function workflowEventProperties(
    workflow: WorkflowEventSource,
    blueprint: WorkflowBlueprint | null,
): WorkflowEventProperties {
    return {
        workflowId: workflow.id,
        entityType: workflow.attachTo,
        status: workflow.status,
        triggerKind: blueprint ? blueprint.triggerKind : UNRECOGNIZED,
        step: blueprint ? stepKey(blueprint.step) : UNRECOGNIZED,
    };
}

export type WizardStepArrival = "open" | "stepper" | "continue" | "back" | "blocker";

export type WizardStepArrivalEvent = {
    wizardStep: string;
    arrivedBy: WizardStepArrival;
};

export type WizardStepViewEventProperties = WorkflowEventProperties &
    WizardStepArrivalEvent;

export function wizardStepViewEventProperties(
    workflow: WorkflowEventSource,
    blueprint: WorkflowBlueprint | null,
    arrival: WizardStepArrivalEvent,
): WizardStepViewEventProperties {
    return {
        ...workflowEventProperties(workflow, blueprint),
        ...arrival,
    };
}

export type WorkflowListEventProperties = {
    workflowCount: number;
    publishedCount: number;
    draftCount: number;
    entityTypes: AdvancedSearchEntityType[];
};

export function workflowListEventProperties(
    workflows: readonly { attachTo: AdvancedSearchEntityType; status: WorkflowStatus }[],
): WorkflowListEventProperties {
    const entityTypes = workflows.map(workflow => workflow.attachTo);
    return {
        workflowCount: workflows.length,
        publishedCount: workflows.filter(
            workflow => workflow.status === WorkflowStatus.Published,
        ).length,
        draftCount: workflows.filter(workflow => workflow.status === WorkflowStatus.Draft)
            .length,
        entityTypes: entityTypes.filter(
            (entityType, index) => entityTypes.indexOf(entityType) === index,
        ),
    };
}

export type BlueprintField = "recordType" | "trigger" | "step";

export type BlueprintChangeEventProperties = {
    workflowId: string;
    field: BlueprintField | null;
    fromEntityType: AdvancedSearchEntityType;
    toEntityType: AdvancedSearchEntityType;
    fromTriggerKind: WorkflowTriggerKind;
    toTriggerKind: WorkflowTriggerKind;
    fromStep: string;
    toStep: string;
};

function changedField(
    from: WorkflowBlueprint,
    to: WorkflowBlueprint,
): BlueprintField | null {
    if (from.entityType !== to.entityType) {
        return "recordType";
    }
    if (from.triggerKind !== to.triggerKind) {
        return "trigger";
    }
    if (stepKey(from.step) !== stepKey(to.step)) {
        return "step";
    }
    return null;
}

export function blueprintChangeEventProperties(
    workflowId: string,
    from: WorkflowBlueprint,
    to: WorkflowBlueprint,
): BlueprintChangeEventProperties {
    return {
        workflowId,
        field: changedField(from, to),
        fromEntityType: from.entityType,
        toEntityType: to.entityType,
        fromTriggerKind: from.triggerKind,
        toTriggerKind: to.triggerKind,
        fromStep: stepKey(from.step),
        toStep: stepKey(to.step),
    };
}
